import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors, type DragEndEvent } from "@dnd-kit/core";
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { Comment, Event, Node, Ref } from "@melo/shared";
import { api } from "../lib/api";
import { store, useTables } from "../lib/store";
import { ago, childrenOf, findByKey, formatDate, isDone, keyOf, kindsOf, nodesOf, statusOf, since } from "../lib/select";
import { archiveNode, checklist, moveNode, send, setStatus, updateNode } from "../lib/actions";
import { useOpenItem } from "../lib/nav";
import { uploadFiles } from "../lib/upload";
import { toast } from "../lib/toast";
import { Avatar, Avatars, Empty, LAYER, useDismiss } from "./primitives";
import { AssigneePicker, DateInput, FieldEditor, MenuButton, StatusChip, StatusDot, StatusPicker, fieldText } from "./pickers";
import { Markdown } from "./Markdown";
import { Icon } from "./icons";
import { describeEvent } from "./activity";
import { RefThumb, Lightbox } from "./RefGrid";
import { ItemLink } from "./ItemLink";
import { can, canEditNode } from "../lib/access";
import { NodeLabels } from "./labels";
import { Link } from "wouter";
import { ShipDialog } from "./Cycle";

export function ItemPanel() {
  const [key, setKey] = useOpenItem();
  const t = useTables();
  const node = key ? findByKey(t, key) : undefined;
  const panel = useRef<HTMLElement>(null);
  const close = () => setKey(null);
  useDismiss(panel, close, Boolean(key), LAYER.panel);
  if (!key) return null;
  return createPortal(
    <>
      <div className="scrim panel-scrim" />
      <aside
        ref={panel}
        className="panel"
        data-layer={LAYER.panel}
        role="dialog"
        aria-modal="true"
        aria-label={node ? node.title : "Item"}
        onPaste={(e) => {
          const files = [...e.clipboardData.files];
          if (!node || !files.length) return;
          e.preventDefault();
          void uploadFiles(files, { spaceId: node.spaceId, nodeId: node.id });
        }}
      >
        {node ? <PanelBody key={node.id} node={node} onClose={close} /> : <Missing k={key} onClose={close} />}
      </aside>
    </>,
    document.body,
  );
}

function Missing({ k, onClose }: { k: string; onClose: () => void }) {
  return (
    <>
      <div className="panel-h">
        <span className="mono muted">{k}</span>
        <button type="button" className="btn ghost icon sm" style={{ marginLeft: "auto" }} onClick={onClose} aria-label="Close">
          <Icon name="x" />
        </button>
      </div>
      <Empty title="This item doesn't exist or was archived." />
    </>
  );
}

function PanelBody({ node, onClose }: { node: Node; onClose: () => void }) {
  const t = useTables();
  const space = t.spaces.get(node.spaceId)!;
  const kind = t.kinds.get(node.kindId);
  const key = keyOf(t, node);
  const claim = t.claims.get(node.id);
  const parent = node.parentId ? t.nodes.get(node.parentId) : undefined;
  const [refsOpen, setRefsOpen] = useState<Ref | null>(null);
  const member = can(node.spaceId, "member");
  const editable = canEditNode(node);
  const cover = node.coverRefId ? t.refs.get(node.coverRefId) : undefined;
  const [shipping, setShipping] = useState(false);

  // Comments come from the detail endpoint; live ones arrive as events.
  useEffect(() => {
    void api.get<{ comments: Comment[] }>(`/v1/nodes/${node.id}`).then((d) => store.putComments(d.comments), () => {});
  }, [node.id]);

  return (
    <>
      <div className="panel-h">
        <span className="mono muted">{key}</span>
        <span className="chip">{space.name}</span>
        {member ? (
          <MenuButton
            label="Change type"
            className="chip"
            items={() => kindsOf(store.t, space.id).map((k) => ({ id: k.id, label: k.name, checked: k.id === node.kindId, run: () => k.id !== node.kindId && void send("node.update", { nodeId: node.id, kind: k.key }) }))}
          >
            {kind?.name ?? "Item"}
          </MenuButton>
        ) : (
          <span className="chip">{kind?.name ?? "Item"}</span>
        )}
        <span className="grow" />
        <button
          type="button"
          className="btn ghost icon sm"
          title="Copy link"
          aria-label="Copy link"
          onClick={() =>
            navigator.clipboard.writeText(`${location.origin}${location.pathname}?item=${key}`).then(
              () => toast("Link copied"),
              () => toast("Could not copy the link.", { tone: "error" }),
            )
          }
        >
          <Icon name="link" />
        </button>
        {member && (
          <MenuButton
            label="More actions"
            className="btn ghost icon sm"
            align="end"
            items={() => [
              ...(kind?.key === "phase" && can(node.spaceId, "producer") && !isDone(store.t, node) ? [{ id: "ship", label: `Ship ${kind.name.toLowerCase()}`, icon: <Icon name="flag" />, run: () => setShipping(true) }] : []),
              { id: "archive", label: "Archive", danger: true, icon: <Icon name="archive" />, run: () => void archiveNode(node).then(onClose) },
            ]}
          >
            <Icon name="more" />
          </MenuButton>
        )}
        <button type="button" className="btn ghost icon sm" onClick={onClose} aria-label="Close">
          <Icon name="x" />
        </button>
      </div>

      <div className="panel-b">
        {cover?.url && (
          <div className="panel-cover">
            <button type="button" className="panel-cover-img" onClick={() => setRefsOpen(cover)} aria-label="Open the cover picture">
              <img src={cover.url} alt="" />
            </button>
            {editable && (
              <button type="button" className="btn sm panel-cover-rm" onClick={() => void updateNode(node, { coverRefId: null })}>
                Remove cover
              </button>
            )}
          </div>
        )}
        {parent && (
          <ItemLink className="parent-link" itemKey={keyOf(t, parent)}>
            <Icon name={t.kinds.get(parent.kindId)?.key === "phase" ? "flag" : "layers"} /> {parent.title}
          </ItemLink>
        )}
        {editable ? <TitleEditor node={node} /> : <h2 className="title-static">{node.title}</h2>}

        {node.draft && <DraftBanner node={node} />}
        {node.blocker && <BlockerBanner node={node} canAnswer={member} />}
        {claim && (
          <div className="claim-line">
            <span className="claim-dot" />
            <Avatar actor={t.actors.get(claim.actorId)} size="sm" />
            <span className="grow">
              <b>{t.actors.get(claim.actorId)?.name}</b> is working on this{claim.note ? `: ${claim.note}` : ""}
              <small>
                Checked in {since(claim.heartbeatAt)} · lease ends {new Date(claim.leaseUntil).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
              </small>
            </span>
            <button type="button" className="btn sm" onClick={() => void send("claim.release", { nodeId: node.id }).then(() => toast("Released. It is back on the board."))}>
              Release
            </button>
          </div>
        )}

        <Facts node={node} editable={editable} member={member} />
        <BodyEditor node={node} editable={editable} />
        <Checklist node={node} editable={editable} />
        <Children node={node} editable={editable} />
        <Refs node={node} onOpen={setRefsOpen} editable={editable} />
        <Details node={node} editable={editable} member={member} />
        <Timeline node={node} />
      </div>
      {refsOpen && <Lightbox refItem={refsOpen} onClose={() => setRefsOpen(null)} />}
      {shipping && <ShipDialog milestone={node} onClose={() => setShipping(false)} />}
    </>
  );
}

/** What everyone looks at first: where it is, who has it, when it is due, how it is tagged. */
function Facts({ node, editable, member }: { node: Node; editable: boolean; member: boolean }) {
  const t = useTables();
  const kind = t.kinds.get(node.kindId);
  const parent = node.parentId ? t.nodes.get(node.parentId) : undefined;
  const priority = kind?.fields.find((f) => f.key === "priority");
  const owners = node.assignees.filter((a) => a.role === "owner");
  return (
    <div className="facts">
      <Fact label="Status">{editable ? <StatusPicker node={node} kind={kind} /> : <StatusChip kind={kind} status={node.status} />}</Fact>
      <Fact label="Owners">{member ? <AssigneePicker node={node} /> : owners.length ? <Avatars actors={owners.map((a) => t.actors.get(a.actorId))} max={6} /> : <span className="muted">No one</span>}</Fact>
      <Fact label="Due">{member ? <DateInput label="Due" placeholder="Add date" value={node.dueDate} onSave={(v) => void updateNode(node, { dueDate: v })} /> : <span>{node.dueDate ? formatDate(node.dueDate) : <span className="muted">No date</span>}</span>}</Fact>
      <Fact label="Labels">
        <NodeLabels node={node} editable={member} />
      </Fact>
      <Fact label="Part of">{member ? <ParentPicker node={node} /> : <span>{parent?.title ?? <span className="muted">Nothing</span>}</span>}</Fact>
      {priority && <Fact label="Priority">{editable ? <FieldEditor node={node} def={priority} /> : <span>{fieldText(priority, node.fields.priority, t.actors) || <span className="muted">None</span>}</span>}</Fact>}
    </div>
  );
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="fact">
      <span className="fact-l">{label}</span>
      <div className="fact-v">{children}</div>
    </div>
  );
}

/**
 * Everything else about the item. Filled-in fields show; empty ones stay out of the way until
 * someone picks them from "Add a field".
 */
function Details({ node, editable, member }: { node: Node; editable: boolean; member: boolean }) {
  const t = useTables();
  const kind = t.kinds.get(node.kindId);
  const [shown, setShown] = useState<Set<string>>(new Set());
  const reviewer = node.assignees.find((a) => a.role === "reviewer");
  const defs = (kind?.fields ?? []).filter((f) => f.key !== "priority");
  const filled = (k: string) => {
    if (k === "reviewer") return Boolean(reviewer);
    if (k === "start") return Boolean(node.startDate);
    const v = node.fields[k];
    return v !== undefined && v !== null && v !== "" && !(Array.isArray(v) && !v.length);
  };
  const all = [{ key: "reviewer", label: "Reviewer" }, { key: "start", label: "Start date" }, ...defs.map((d) => ({ key: d.key, label: d.label }))];
  const visible = all.filter((f) => filled(f.key) || shown.has(f.key));
  const hidden = all.filter((f) => !visible.includes(f));
  const out = [...t.edges.values()].some((e) => e.fromId === node.id || e.toId === node.id);
  const [linking, setLinking] = useState(false);
  if (!visible.length && !(editable && hidden.length) && !out && !member) return null;
  return (
    <section className="psec">
      <div className="psec-h">
        <h3>Details</h3>
        <span className="grow" />
        {editable && hidden.length > 0 && (
          <MenuButton label="Add a field" className="btn ghost sm" align="end" items={() => hidden.map((f) => ({ id: f.key, label: f.label, run: () => setShown((s) => new Set([...s, f.key])) }))}>
            <Icon name="plus" /> Add a field
          </MenuButton>
        )}
        {member && !out && !linking && (
          <button type="button" className="btn ghost sm" onClick={() => setLinking(true)}>
            <Icon name="link" /> Link an item
          </button>
        )}
      </div>
      {visible.length > 0 && (
        <dl className="props">
          {visible.map((f) =>
            f.key === "reviewer" ? (
              <FieldRow key={f.key} label="Reviewer">
                {member ? <AssigneePicker node={node} role="reviewer" /> : <span className="row-gap"><Avatar actor={t.actors.get(reviewer!.actorId)} size="sm" />{t.actors.get(reviewer!.actorId)?.name}</span>}
              </FieldRow>
            ) : f.key === "start" ? (
              <FieldRow key={f.key} label="Start date">
                {member ? <DateInput label="Start" placeholder="Add date" value={node.startDate} onSave={(v) => void updateNode(node, { startDate: v })} /> : <span>{formatDate(node.startDate)}</span>}
              </FieldRow>
            ) : (
              <FieldRow key={f.key} label={f.label}>
                {editable ? <FieldEditor node={node} def={defs.find((d) => d.key === f.key)!} /> : <span>{fieldText(defs.find((d) => d.key === f.key)!, node.fields[f.key], t.actors) || <span className="muted">–</span>}</span>}
              </FieldRow>
            ),
          )}
        </dl>
      )}
      {member && (out || linking) && <Links node={node} />}
    </section>
  );
}

function FieldRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </>
  );
}

/** A Trello-style checklist: steps inside this item, ticked off by whoever does them. */
function Checklist({ node, editable }: { node: Node; editable: boolean }) {
  const items = node.checklist;
  const [adding, setAdding] = useState(false);
  const [text, setText] = useState("");
  const done = items.filter((c) => c.done).length;
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));
  if (!items.length && !adding) {
    return editable ? (
      <button type="button" className="btn ghost sm add-sec" onClick={() => setAdding(true)}>
        <Icon name="checkbox" /> Add a checklist
      </button>
    ) : null;
  }
  const add = () => {
    const v = text.trim();
    if (!v) return;
    setText("");
    void checklist(node, { op: "add", nodeId: node.id, text: v });
  };
  const onEnd = (e: DragEndEvent) => {
    if (!e.over || e.active.id === e.over.id) return;
    void checklist(node, { op: "move", nodeId: node.id, itemId: String(e.active.id), index: items.findIndex((c) => c.id === e.over!.id) });
  };
  return (
    <section className="psec" aria-label="Checklist">
      <div className="psec-h">
        <h3>Checklist</h3>
        <span className="muted mono">
          {done}/{items.length}
        </span>
      </div>
      {items.length > 0 && (
        <div className="check-meter" aria-hidden="true">
          <i style={{ width: `${(done / items.length) * 100}%` }} className={done === items.length ? "full" : ""} />
        </div>
      )}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onEnd}>
        <SortableContext items={items.map((c) => c.id)} strategy={verticalListSortingStrategy}>
          <div className="checklist">
            {items.map((c) => (
              <CheckRow key={c.id} node={node} item={c} editable={editable} />
            ))}
          </div>
        </SortableContext>
      </DndContext>
      {editable && (
        <input
          className="input sub-add"
          autoFocus={adding && !items.length}
          placeholder="Add an item"
          aria-label="Add a checklist item"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") add();
            if (e.key === "Escape" && !text) (e.stopPropagation(), setAdding(false));
          }}
          onBlur={() => !text && !items.length && setAdding(false)}
        />
      )}
    </section>
  );
}

function CheckRow({ node, item, editable }: { node: Node; item: { id: string; text: string; done: boolean }; editable: boolean }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id, disabled: !editable });
  const [v, setV] = useState(item.text);
  useEffect(() => setV(item.text), [item.text]);
  const save = () => {
    const text = v.trim();
    if (!text) return setV(item.text);
    if (text !== item.text) void checklist(node, { op: "set", nodeId: node.id, itemId: item.id, text });
  };
  return (
    <div ref={setNodeRef} className={`check-row${isDragging ? " dragging" : ""}`} style={{ transform: CSS.Translate.toString(transform), transition }}>
      {editable && (
        <span className="grip" {...attributes} {...listeners} aria-label={`Reorder ${item.text}`}>
          <Icon name="grip" />
        </span>
      )}
      <button
        type="button"
        className={`sub-check${item.done ? " done" : ""}`}
        disabled={!editable}
        aria-label={`${item.done ? "Uncheck" : "Check"} ${item.text}`}
        aria-pressed={item.done}
        onClick={() => void checklist(node, { op: "set", nodeId: node.id, itemId: item.id, done: !item.done })}
      />
      {editable ? (
        <input className={`check-text${item.done ? " done" : ""}`} value={v} aria-label={`Checklist item ${item.text}`} onChange={(e) => setV(e.target.value)} onBlur={save} onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()} />
      ) : (
        <span className={`check-text${item.done ? " done" : ""}`}>{item.text}</span>
      )}
      {editable && (
        <button type="button" className="btn ghost icon sm check-rm" aria-label={`Remove ${item.text}`} onClick={() => void checklist(node, { op: "remove", nodeId: node.id, itemId: item.id })}>
          <Icon name="x" />
        </button>
      )}
    </div>
  );
}

function TitleEditor({ node }: { node: Node }) {
  const [v, setV] = useState(node.title);
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => setV(node.title), [node.title]);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "0";
    el.style.height = `${el.scrollHeight}px`;
  }, [v]);
  const save = () => {
    const title = v.trim();
    if (!title) return setV(node.title);
    if (title !== node.title) void updateNode(node, { title });
  };
  return (
    <textarea
      ref={ref}
      className="title-input"
      value={v}
      rows={1}
      aria-label="Title"
      onChange={(e) => setV(e.target.value.replace(/\n/g, " "))}
      onBlur={save}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          (e.target as HTMLTextAreaElement).blur();
        }
      }}
    />
  );
}

function DraftBanner({ node }: { node: Node }) {
  const t = useTables();
  const plan = node.planId ? t.plans.get(node.planId) : undefined;
  return (
    <div className="banner">
      <Icon name="bot" />
      <span className="grow">
        <b>Draft.</b> Proposed by {t.actors.get(plan?.proposedBy ?? node.createdBy)?.name ?? "an agent"}{plan ? ` in "${plan.summary}"` : ""}. It stays off boards until a person approves the plan.
      </span>
      {plan && (
        <Link className="btn sm" href={`/?plan=${plan.id}`}>
          Review plan
        </Link>
      )}
    </div>
  );
}

function BlockerBanner({ node, canAnswer }: { node: Node; canAnswer: boolean }) {
  const t = useTables();
  const b = node.blocker!;
  const [answer, setAnswer] = useState("");
  const who = t.actors.get(b.actorId);
  const unblock = (text?: string) => void send("node.unblock", { nodeId: node.id, answer: text || undefined }).then(() => toast(text ? "Answered. The item is unblocked." : "Unblocked"));
  return (
    <div className="banner signal">
      <div className="banner-top">
        <span className="chip sig">{b.kind === "needs_input" ? "Needs input" : b.kind === "dependency" ? "Waiting on another item" : b.kind === "capability" ? "Needs a capability" : "Blocked"}</span>
        <span className="muted">
          {who?.name} · {since(b.at)}
        </span>
      </div>
      <p className="banner-q">{b.reason}</p>
      {!canAnswer ? (
        <p className="hint">A producer or teammate will answer this.</p>
      ) : b.options?.length ? (
        <div className="banner-options">
          {b.options.map((o) => (
            <button key={o} type="button" className="btn sm" onClick={() => unblock(o)}>
              {o}
            </button>
          ))}
        </div>
      ) : null}
      {canAnswer && <div className="banner-answer">
        <input className="input" placeholder={b.kind === "needs_input" ? "Answer in your own words" : "Note (optional)"} value={answer} onChange={(e) => setAnswer(e.target.value)} onKeyDown={(e) => e.key === "Enter" && unblock(answer)} aria-label="Answer" />
        <button type="button" className="btn pri sm" onClick={() => unblock(answer)}>
          {answer ? "Answer" : "Unblock"}
        </button>
      </div>}
    </div>
  );
}

function ParentPicker({ node }: { node: Node }) {
  const t = useTables();
  const parent = node.parentId ? t.nodes.get(node.parentId) : undefined;
  return (
    <MenuButton
      label="Change parent"
      className="chip"
      searchable
      width={320}
      items={() => {
        // Anything in the space that is not this item or inside it.
        const inside = new Set<string>([node.id]);
        let grew = true;
        const all = nodesOf(store.t, node.spaceId);
        while (grew) {
          grew = false;
          for (const n of all) if (n.parentId && inside.has(n.parentId) && !inside.has(n.id)) (inside.add(n.id), (grew = true));
        }
        return [
          { id: "__none", label: "No parent", checked: !node.parentId, run: () => node.parentId && void moveNode(node, { parentId: null }) },
          ...all.filter((n) => !inside.has(n.id)).map((n) => ({ id: n.id, label: n.title, hint: keyOf(store.t, n), checked: n.id === node.parentId, run: () => n.id !== node.parentId && void moveNode(node, { parentId: n.id }) })),
        ];
      }}
    >
      {parent ? parent.title : <span className="muted">None</span>}
    </MenuButton>
  );
}

function BodyEditor({ node, editable }: { node: Node; editable: boolean }) {
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(node.body);
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    if (!editing) setV(node.body);
  }, [node.body, editing]);
  useEffect(() => {
    if (!editing || !ref.current) return;
    ref.current.style.height = "0";
    ref.current.style.height = `${Math.max(140, ref.current.scrollHeight)}px`;
  }, [v, editing]);
  const save = () => {
    setEditing(false);
    if (v !== node.body) void updateNode(node, { body: v });
  };
  return (
    <section className="psec">
      <div className="psec-h">
        <h3>Notes</h3>
        {!editing && editable && (
          <button type="button" className="btn ghost sm" onClick={() => setEditing(true)}>
            <Icon name="edit" /> Edit
          </button>
        )}
      </div>
      {editing ? (
        <>
          <textarea
            ref={ref}
            autoFocus
            className="textarea body-input"
            value={v}
            onChange={(e) => setV(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) save();
              if (e.key === "Escape") {
                e.stopPropagation();
                setV(node.body);
                setEditing(false);
              }
            }}
            placeholder="Markdown. Describe the goal, the design, and what done looks like."
            aria-label="Notes"
          />
          <div className="form-actions" style={{ marginTop: 8 }}>
            <span className="hint grow">Markdown · ⌘ Enter to save</span>
            <button type="button" className="btn ghost sm" onClick={() => (setV(node.body), setEditing(false))}>
              Cancel
            </button>
            <button type="button" className="btn pri sm" onClick={save}>
              Save
            </button>
          </div>
        </>
      ) : node.body ? (
        <div className="body-view" onDoubleClick={() => editable && setEditing(true)}>
          <Markdown text={node.body} />
        </div>
      ) : editable ? (
        <button type="button" className="body-empty" onClick={() => setEditing(true)}>
          Add notes
        </button>
      ) : (
        <p className="hint">No notes.</p>
      )}
    </section>
  );
}

function Children({ node, editable }: { node: Node; editable: boolean }) {
  const t = useTables();
  const kids = childrenOf(t, node.id);
  const done = kids.filter((k) => isDone(t, k)).length;
  const [title, setTitle] = useState("");
  const [adding, setAdding] = useState(false);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));
  // New children take the kind of the existing ones, or "task".
  const childKind = useMemo(() => {
    const first = kids[0] ? t.kinds.get(kids[0].kindId) : undefined;
    return first?.key ?? "task";
  }, [t, kids]);
  const add = () => {
    const text = title.trim();
    if (!text) return;
    setTitle("");
    void send("node.create", { spaceId: node.spaceId, nodes: [{ kind: childKind, title: text, parentId: node.id, ...(kids.at(-1) ? { afterId: kids.at(-1)!.id } : {}) }] });
  };
  const onEnd = (e: DragEndEvent) => {
    if (!e.over || e.active.id === e.over.id) return;
    const ids = kids.map((k) => k.id);
    const from = ids.indexOf(String(e.active.id));
    const to = ids.indexOf(String(e.over.id));
    const moving = kids[from]!;
    const rest = ids.filter((x) => x !== moving.id);
    rest.splice(to, 0, moving.id);
    const i = rest.indexOf(moving.id);
    void moveNode(moving, rest[i - 1] ? { afterId: rest[i - 1] } : { beforeId: rest[i + 1] });
  };
  // Name the section after what is inside: "Upgrades" under a tower family, "Subtasks" under a task.
  const kidKinds = new Set(kids.map((k) => k.kindId));
  const only = kidKinds.size === 1 ? t.kinds.get([...kidKinds][0]!) : undefined;
  const isMilestone = t.kinds.get(node.kindId)?.key === "phase";
  const word = t.kinds.get(node.kindId)?.name.toLowerCase() ?? "milestone";
  const heading = isMilestone ? `Work in this ${word}` : !kids.length || only?.key === "task" ? "Subtasks" : only ? `${only.name}s` : "Inside";
  const addLabel = isMilestone ? "Add work" : childKind === "task" ? "Add a subtask" : `Add ${childKind}`;
  // Subtasks are separate cards someone else can own. Until there are some, they are one button.
  if (!kids.length && !adding) {
    return editable ? (
      <button type="button" className="btn ghost sm add-sec" onClick={() => setAdding(true)}>
        <Icon name="layers" /> {isMilestone ? `Add work to this ${word}` : "Split into subtasks"}
      </button>
    ) : null;
  }
  return (
    <section className="psec">
      <div className="psec-h">
        <h3>{heading}</h3>
        {kids.length > 0 && (
          <span className="muted mono">
            {done}/{kids.length}
          </span>
        )}
      </div>
      {kids.length > 0 && (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onEnd}>
          <SortableContext items={kids.map((k) => k.id)} strategy={verticalListSortingStrategy}>
            <div className="subs">
              {kids.map((k) => (
                <SubRow key={k.id} node={k} editable={editable} />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
      {editable && (
        <input
          className="input sub-add"
          autoFocus={adding && !kids.length}
          placeholder={addLabel}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") add();
            if (e.key === "Escape" && !title) (e.stopPropagation(), setAdding(false));
          }}
          onBlur={() => !title && !kids.length && setAdding(false)}
          aria-label={addLabel}
        />
      )}
    </section>
  );
}

function SubRow({ node, editable }: { node: Node; editable: boolean }) {
  const t = useTables();
  const kind = t.kinds.get(node.kindId);
  const def = statusOf(kind, node.status);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: node.id });
  const toggle = () => {
    const target = def?.category === "done" ? kind?.statuses.find((s) => s.category === "todo") : kind?.statuses.find((s) => s.category === "done");
    if (target) void setStatus(node, target.key);
  };
  return (
    <div ref={setNodeRef} className={`sub${isDragging ? " dragging" : ""}`} style={{ transform: CSS.Translate.toString(transform), transition }}>
      <span className="grip" {...attributes} {...listeners} aria-label={`Reorder ${node.title}`}>
        <Icon name="grip" />
      </span>
      <button type="button" className={`sub-check${def?.category === "done" ? " done" : ""}`} onClick={toggle} disabled={!editable} aria-label={def?.category === "done" ? "Mark not done" : "Mark done"} />
      <ItemLink className={`sub-title${def?.category === "done" ? " done" : ""}`} itemKey={keyOf(t, node)}>
        {node.title}
      </ItemLink>
      <span className="chip">
        <StatusDot status={def} />
        {def?.label}
      </span>
      {node.assignees[0] && <Avatar actor={t.actors.get(node.assignees[0].actorId)} size="sm" />}
    </div>
  );
}

function Refs({ node, onOpen, editable }: { node: Node; onOpen: (r: Ref) => void; editable: boolean }) {
  const t = useTables();
  const refs = [...t.refs.values()].filter((r) => r.nodeId === node.id).sort((a, b) => (a.rank < b.rank ? -1 : 1));
  const [over, setOver] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const upload = (files: File[]) => editable && files.length && void uploadFiles(files, { spaceId: node.spaceId, nodeId: node.id });
  return (
    <section
      className={`psec drop${over ? " over" : ""}`}
      onDragOver={(e) => {
        if (editable && e.dataTransfer.types.includes("Files")) {
          e.preventDefault();
          setOver(true);
        }
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        upload([...e.dataTransfer.files]);
      }}
    >
      <div className="psec-h">
        <h3>Attachments</h3>
        <span className="muted mono">{refs.length || ""}</span>
        <span className="grow" />
        {editable && (
          <button type="button" className="btn ghost sm" onClick={() => input.current?.click()}>
            <Icon name="upload" /> Add
          </button>
        )}
        <input ref={input} type="file" multiple hidden onChange={(e) => (upload([...(e.target.files ?? [])]), (e.target.value = ""))} />
      </div>
      {refs.length ? (
        <div className="ref-strip">
          {refs.map((r) => (
            <RefThumb key={r.id} refItem={r} cover={node.coverRefId === r.id} onOpen={() => onOpen(r)} onCover={r.type === "image" && editable ? () => void updateNode(node, { coverRefId: node.coverRefId === r.id ? null : r.id }) : undefined} />
          ))}
        </div>
      ) : editable ? (
        <button type="button" className="body-empty" onClick={() => input.current?.click()}>
          Drop pictures or files here, or paste a screenshot. The first picture becomes the cover.
        </button>
      ) : (
        <p className="hint">No attachments.</p>
      )}
    </section>
  );
}

function Links({ node }: { node: Node }) {
  const t = useTables();
  const out = [...t.edges.values()].filter((e) => e.fromId === node.id);
  const inc = [...t.edges.values()].filter((e) => e.toId === node.id);
  const label = { blocks: ["Blocks", "Blocked by"], implements: ["Implements", "Implemented by"], relates: ["Related to", "Related to"] } as const;
  const rows = [...out.map((e) => ({ e, other: t.nodes.get(e.toId), text: label[e.type][0] })), ...inc.map((e) => ({ e, other: t.nodes.get(e.fromId), text: label[e.type][1] }))].filter((r) => r.other);
  const addItems = (type: "blocks" | "implements" | "relates") => () =>
    [...store.t.nodes.values()]
      .filter((n) => n.id !== node.id && !n.draft)
      .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
      .slice(0, 400)
      .map((n) => ({ id: n.id, label: n.title, hint: keyOf(store.t, n), run: () => void send("edge.add", { fromId: node.id, toId: n.id, type }) }));
  return (
    <section className="psec">
      <div className="psec-h">
        <h3>Links</h3>
        <MenuButton label="Link another item" className="btn ghost sm" searchable width={340} items={addItems("relates")}>
          <Icon name="link" /> Relate
        </MenuButton>
        <MenuButton label="Mark an item this blocks" className="btn ghost sm" searchable width={340} items={addItems("blocks")}>
          Blocks
        </MenuButton>
        <MenuButton label="Link a concept this implements" className="btn ghost sm" searchable width={340} items={addItems("implements")}>
          Implements
        </MenuButton>
      </div>
      {rows.length > 0 && (
        <div className="links">
          {rows.map(({ e, other, text }) => (
            <div key={e.id} className="link-row">
              <span className="muted link-type">{text}</span>
              <ItemLink itemKey={keyOf(t, other!)} className="grow">
                <span className="mono muted">{keyOf(t, other!)}</span> {other!.title}
              </ItemLink>
              <button type="button" className="btn ghost icon sm" aria-label="Remove link" onClick={() => void send("edge.remove", { edgeId: e.id })}>
                <Icon name="x" />
              </button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

/** Comments first, the way people talk about a card. Every change is one click away under "Show history". */
function Timeline({ node }: { node: Node }) {
  const t = useTables();
  const [text, setText] = useState("");
  const [history, setHistory] = useState(false);
  const comments = [...t.comments.values()].filter((c) => c.nodeId === node.id);
  const events = store.feed.filter((e) => e.entity === "node" && e.entityId === node.id && e.command !== "comment.add" && !e.summary.auto);
  type Item = { at: string; seq: number } & ({ c: Comment } | { e: Event });
  const items: Item[] = [...comments.map((c) => ({ at: c.createdAt, seq: c.seq, c })), ...(history ? events.map((e) => ({ at: e.createdAt, seq: e.seq, e })) : [])].sort((a, b) => b.seq - a.seq).slice(0, 80);
  const post = () => {
    const body = text.trim();
    if (!body) return;
    setText("");
    void send("comment.add", { nodeId: node.id, body });
  };
  return (
    <section className="psec">
      <div className="psec-h">
        <h3>Comments</h3>
        <span className="muted mono">{comments.length || ""}</span>
        <span className="grow" />
        {events.length > 0 && (
          <button type="button" className="btn ghost sm" aria-pressed={history} onClick={() => setHistory((v) => !v)}>
            {history ? "Hide history" : `Show history (${events.length})`}
          </button>
        )}
      </div>
      <div className="composer">
        <textarea className="textarea" rows={2} placeholder="Write a comment. ⌘ Enter to post." value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.metaKey || e.ctrlKey) && post()} aria-label="Comment" />
        <button type="button" className="btn pri sm" onClick={post} disabled={!text.trim()}>
          Comment
        </button>
      </div>
      <div className="timeline">
        {items.map((i) =>
          "c" in i ? (
            <div key={`c${i.c.id}`} className="tl-comment">
              <Avatar actor={t.actors.get(i.c.actorId)} size="sm" />
              <div className="grow">
                <div className="tl-meta">
                  <b>{t.actors.get(i.c.actorId)?.name}</b> <span className="muted">{ago(i.c.createdAt)}</span>
                </div>
                <Markdown text={i.c.body} />
              </div>
            </div>
          ) : (
            <div key={`e${i.e.seq}`} className="tl-event">
              <span className="tl-dot" />
              <span className="grow">
                <b>{t.actors.get(i.e.actorId)?.name ?? "Someone"}</b> {describeEvent(t, i.e, true)}
                {i.e.via !== "web" && <span className="via">{i.e.via}</span>}
              </span>
              <span className="muted mono">{ago(i.e.createdAt)}</span>
            </div>
          ),
        )}
        {!comments.length && !history && <p className="hint">No comments yet. Created {formatDate(node.createdAt.slice(0, 10))} by {t.actors.get(node.createdBy)?.name}.</p>}
      </div>
    </section>
  );
}
